import { Component } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';

import BriefInput from './BriefInput';
import FilterReview from './FilterReview';
import PlanPreviewPanel from './PlanPreviewPanel';
import { postBriefToFilters, postPlanPreview } from '../../../../api_handlers/ai';

/**
 * Turns a campaign brief into reviewable discovery filters, and drafts a plan
 * from whatever the user has shortlisted.
 *
 * Deliberately a separate mode from the existing server-side "AI Mode": there,
 * free text goes straight to the backend. Here the user sees and edits the
 * filters before anything is searched.
 */
class BriefAssistant extends Component {
  constructor(props) {
    super(props);
    this.state = {
      brief: '',
      analysis: null,
      selectedKeys: [],
      analysing: false,
      analyseError: null,
      plan: null,
      planning: false,
      planError: null,
    };
  }

  async analyse() {
    this.setState({ analysing: true, analyseError: null });

    try {
      const analysis = await postBriefToFilters(this.state.brief, this.props.campaign);

      this.setState({
        analysis,
        // Everything on by default; the user unticks what they disagree with.
        selectedKeys: Object.keys(analysis.plannerFilters || {}),
        analysing: false,
      });
    } catch (error) {
      this.setState({ analysing: false, analyseError: error.message, analysis: null });
    }
  }

  toggleKey(key) {
    this.setState((previous) => ({
      selectedKeys: previous.selectedKeys.includes(key)
        ? previous.selectedKeys.filter((entry) => entry !== key)
        : [...previous.selectedKeys, key],
    }));
  }

  applyFilters() {
    const { analysis, selectedKeys } = this.state;
    if (!analysis) return;

    const chosen = selectedKeys.reduce((accumulator, key) => {
      return { ...accumulator, [key]: analysis.plannerFilters[key] };
    }, {});

    this.props.onApplyFilters(chosen, this.state.brief);
  }

  async buildPlan() {
    const { selection } = this.props;
    this.setState({ planning: true, planError: null });

    try {
      const plan = await postPlanPreview(
        this.state.brief,
        selection,
        this.state.analysis ? this.state.analysis.filters.budget : null,
        this.state.analysis ? this.state.analysis.plannerFilters : null,
      );
      this.setState({ plan, planning: false });
    } catch (error) {
      this.setState({ planning: false, planError: error.message, plan: null });
    }
  }

  renderPlanSection() {
    const { selection } = this.props;
    const { planning, planError, plan, brief } = this.state;

    return (
      <Box sx={{ mt: 3 }}>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="subtitle2">Plan preview</Typography>

        {selection.length === 0 ? (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
            Select creators in the results to draft a plan from them.
          </Typography>
        ) : (
          <Button
            variant="outlined"
            size="small"
            sx={{ mt: 1 }}
            disabled={planning || brief.trim().length === 0}
            onClick={() => this.buildPlan()}
            startIcon={planning ? <CircularProgress size={14} /> : null}
          >
            {planning
              ? 'Drafting…'
              : `Draft a plan from ${selection.length} creator${selection.length === 1 ? '' : 's'}`}
          </Button>
        )}

        {planError && (
          <Alert
            severity="error"
            sx={{ mt: 1.5 }}
            action={
              <Button size="small" color="inherit" onClick={() => this.buildPlan()}>
                Retry
              </Button>
            }
          >
            {planError}
          </Alert>
        )}

        <PlanPreviewPanel plan={plan} />
      </Box>
    );
  }

  render() {
    const { open, onClose } = this.props;
    const { brief, analysis, selectedKeys, analysing, analyseError } = this.state;

    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: 460 }, p: 2.5 } }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <DescriptionIcon fontSize="small" />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Search from a brief
            </Typography>
          </Stack>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          Filters are proposed, not applied. Review them before searching.
        </Typography>

        <BriefInput
          value={brief}
          onChange={(value) => this.setState({ brief: value })}
          onAnalyse={() => this.analyse()}
          loading={analysing}
        />

        {analyseError && (
          <Alert
            severity="error"
            sx={{ mt: 2 }}
            action={
              <Button size="small" color="inherit" onClick={() => this.analyse()}>
                Retry
              </Button>
            }
          >
            {analyseError}
          </Alert>
        )}

        {analysis && (
          <FilterReview
            plannerFilters={analysis.plannerFilters}
            filterLabels={analysis.filterLabels}
            rationale={analysis.rationale}
            assumptions={analysis.assumptions}
            clarifyingQuestions={analysis.clarifyingQuestions}
            confidence={analysis.confidence}
            selectedKeys={selectedKeys}
            onToggleKey={(key) => this.toggleKey(key)}
            onApply={() => this.applyFilters()}
          />
        )}

        {this.renderPlanSection()}
      </Drawer>
    );
  }
}

export default BriefAssistant;
